import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Menu, X, ChevronDown } from 'lucide-react';
import { useCart } from '../../context/CartContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const navigate = useNavigate();
  const { cartCount } = useCart();

  const categories = [
    { id: 'honey', name: 'Honey', icon: '🍯' },
    { id: 'dates', name: 'Dates', icon: '🌴' },
    { id: 'seeds', name: 'Seeds', icon: '🌱' },
    { id: 'dry-fruits', name: 'Dry Fruits', icon: '🥜' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-[#1F7A63] text-white text-center py-2 px-4 text-sm" data-testid="announcement-bar">
        <p className="font-medium">
          Cash on Delivery Available All Over Pakistan | 100% Pure & Organic
        </p>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass shadow-soft' : 'bg-[#FFF8E7]/95 backdrop-blur-sm'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <span className="text-2xl md:text-3xl font-black tracking-tighter text-[#1F7A63]">
                Nature<span className="text-[#E0A106]">Wayz</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              <Link
                to="/"
                className="text-[#2C1E0F] font-medium hover:text-[#1F7A63] transition-colors"
                data-testid="nav-home"
              >
                Home
              </Link>
              <Link
                to="/shop"
                className="text-[#2C1E0F] font-medium hover:text-[#1F7A63] transition-colors"
                data-testid="nav-shop"
              >
                Shop
              </Link>

              {/* Categories Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsCategoryOpen(true)}
                onMouseLeave={() => setIsCategoryOpen(false)}
              >
                <button
                  onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                  className="flex items-center gap-1 text-[#2C1E0F] font-medium hover:text-[#1F7A63] transition-colors"
                  data-testid="nav-categories-dropdown"
                  aria-expanded={isCategoryOpen}
                  aria-haspopup="true"
                >
                  Categories
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`} />
                </button>
                {isCategoryOpen && (
                  <div
                    className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-xl border border-[#6B4F2A]/10 py-4 min-w-[220px] z-50"
                    data-testid="categories-dropdown-menu"
                  >
                    {categories.map((category) => (
                      <Link
                        key={category.id}
                        to={`/shop?category=${category.id}`}
                        onClick={() => setIsCategoryOpen(false)}
                        className="flex items-center gap-3 px-5 py-3 hover:bg-[#FFF8E7] transition-colors"
                        data-testid={`category-link-${category.id}`}
                      >
                        <span className="text-xl">{category.icon}</span>
                        <span className="text-[#2C1E0F] font-medium">{category.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className="text-[#2C1E0F] font-medium hover:text-[#1F7A63] transition-colors"
                data-testid="nav-about"
              >
                About Us
              </Link>
              <Link
                to="/contact"
                className="text-[#2C1E0F] font-medium hover:text-[#1F7A63] transition-colors"
                data-testid="nav-contact"
              >
                Contact
              </Link>
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className="p-2 hover:bg-[#1F7A63]/10 rounded-full transition-colors"
                  data-testid="search-toggle-btn"
                >
                  <Search className="w-5 h-5 text-[#2C1E0F]" />
                </button>

                {/* Search Input */}
                {isSearchOpen && (
                  <form
                    onSubmit={handleSearch}
                    className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl shadow-xl border border-[#6B4F2A]/10 p-3"
                    data-testid="search-form"
                  >
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search products..."
                        className="flex-1 px-4 py-2 rounded-xl border border-[#6B4F2A]/20 focus:outline-none focus:ring-2 focus:ring-[#1F7A63]"
                        autoFocus
                        data-testid="search-input"
                      />
                      <button
                        type="submit"
                        className="px-4 py-2 bg-[#1F7A63] text-white rounded-xl hover:bg-[#155a48] transition-colors"
                        data-testid="search-submit-btn"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Cart */}
              <Link
                to="/cart"
                className="relative p-2 hover:bg-[#1F7A63]/10 rounded-full transition-colors"
                data-testid="cart-link"
              >
                <ShoppingCart className="w-5 h-5 text-[#2C1E0F]" />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-5 h-5 bg-[#E0A106] text-[#2C1E0F] text-xs font-bold rounded-full flex items-center justify-center"
                    data-testid="cart-count"
                  >
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 hover:bg-[#1F7A63]/10 rounded-full transition-colors"
                data-testid="mobile-menu-toggle"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-[#2C1E0F]" />
                ) : (
                  <Menu className="w-6 h-6 text-[#2C1E0F]" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden fixed inset-0 top-[104px] bg-white z-50 transform transition-transform duration-300 ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col p-6 gap-4">
            <Link
              to="/"
              onClick={() => setIsMenuOpen(false)}
              className="text-lg font-medium text-[#2C1E0F] py-3 border-b border-[#6B4F2A]/10"
              data-testid="mobile-nav-home"
            >
              Home
            </Link>
            <Link
              to="/shop"
              onClick={() => setIsMenuOpen(false)}
              className="text-lg font-medium text-[#2C1E0F] py-3 border-b border-[#6B4F2A]/10"
              data-testid="mobile-nav-shop"
            >
              Shop
            </Link>

            <div className="py-3 border-b border-[#6B4F2A]/10">
              <p className="text-lg font-medium text-[#2C1E0F] mb-3">Categories</p>
              <div className="flex flex-col gap-2 pl-4">
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/shop?category=${category.id}`}
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 py-2 text-[#574021]"
                    data-testid={`mobile-category-${category.id}`}
                  >
                    <span>{category.icon}</span>
                    <span>{category.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/about"
              onClick={() => setIsMenuOpen(false)}
              className="text-lg font-medium text-[#2C1E0F] py-3 border-b border-[#6B4F2A]/10"
              data-testid="mobile-nav-about"
            >
              About Us
            </Link>
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="text-lg font-medium text-[#2C1E0F] py-3 border-b border-[#6B4F2A]/10"
              data-testid="mobile-nav-contact"
            >
              Contact
            </Link>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
